/** Prexo App v5.9.4 **/
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronDown, ChevronUp, Plus, Minus, Sun, Moon, Edit2, Trash2, Save, X, Upload, Download, FileSpreadsheet, AlertTriangle, RotateCcw, LogOut, User, Shield, Search } from 'lucide-react';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// Imports Modulares
import { supabase, fetchCurrentUser, logoutUser } from './services/supabase';
import { PRODUTOS_INICIAL, EMBALAGENS_INICIAL, INSUMOS_INICIAL, CANAIS_INICIAL } from './data/initialData';
import { formatBRL, formatPercent } from './utils/formatters';
import { aplicarDesconto } from './utils/calculations';
import { precoDeVenda, resultadoNoPreco } from './utils/precoEngine';
import AuthModal from './components/AuthModal';
import { syncCollection, saveItem, deleteItem } from './services/dataSync';
import WaitingApproval from './components/WaitingApproval';
import AdminPanel from './components/AdminPanel';
import UserProfileModal from './components/UserProfileModal';
import About from './components/About';
import LandingPage from './components/LandingPage';




// Global theme proxy for CSS variables
const t = {
  bg: 'var(--bg)',
  bgCard: 'var(--bg-card)',
  bgCardHover: 'var(--bg-card-hover)',
  bgInput: 'var(--bg-input)',
  border: 'var(--border)',
  borderLight: 'var(--border-light)',
  text: 'var(--text)',
  textSecondary: 'var(--text-secondary)',
  textMuted: 'var(--text-muted)',
  accent: 'var(--accent)',
  success: 'var(--success)',
  danger: 'var(--danger)',
  sliderTrack: 'var(--slider-track)',
  // tokens do tema Extrato
  ink: 'var(--ink)',
  lineDot: 'var(--line-dot)',
  meli: 'var(--meli)',
  meliInk: 'var(--meli-ink)',
  shopee: 'var(--shopee)',
  profit: 'var(--profit)',
  mono: "'JetBrains Mono', ui-monospace, monospace",
  disp: "'Space Grotesk', -apple-system, system-ui, sans-serif",
};

// Componente Slider customizado
const Slider = ({ value, onChange, min, max, darkMode }) => {
  const percent = ((value - min) / (max - min)) * 100;

  return (
    <div style={{ position: 'relative', width: '100%', height: 20, display: 'flex', alignItems: 'center' }}>
      {/* Track de fundo */}
      <div style={{
        position: 'absolute',
        left: 0,
        right: 0,
        height: 4,
        borderRadius: 2,
        background: t.sliderTrack,
      }} />
      {/* Track preenchido */}
      <div style={{
        position: 'absolute',
        left: 0,
        width: `${percent}%`,
        height: 4,
        borderRadius: 2,
        background: t.accent,
      }} />
      {/* Input invisível para capturar eventos */}
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={onChange}
        style={{
          position: 'absolute',
          width: '100%',
          height: 20,
          opacity: 0,
          cursor: 'pointer',
          margin: 0,
        }}
      />
      {/* Thumb visual */}
      <div style={{
        position: 'absolute',
        left: `calc(${percent}% - 8px)`,
        width: 16,
        height: 16,
        borderRadius: 8,
        background: t.accent,
        border: '2px solid #fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        pointerEvents: 'none',
      }} />
    </div>
  );
};

// ==================== COMPONENTES ====================

// Dropdown Seletor
const Dropdown = ({ items, selecionados, onUpdate, label, darkMode, showUnidade }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);

  const total = selecionados.reduce((acc, s) => acc + (s.custo * s.qtd), 0);
  const qtdItens = selecionados.reduce((acc, s) => acc + s.qtd, 0);

  const addItem = (item) => {
    const existe = selecionados.find(s => String(s.id) === String(item.id));
    if (existe) {
      onUpdate(selecionados.map(s => String(s.id) === String(item.id) ? { ...s, qtd: s.qtd + 1 } : s));
    } else {
      onUpdate([...selecionados, { ...item, qtd: 1 }]);
    }
  };

  const updateQtd = (id, delta) => {
    onUpdate(selecionados.map(s => {
      if (String(s.id) === String(id)) {
        const newQtd = Math.max(0, s.qtd + delta);
        return newQtd === 0 ? null : { ...s, qtd: newQtd };
      }
      return s;
    }).filter(Boolean));
  };

  return (
    <div ref={containerRef} style={{ borderBottom: `1.5px dashed ${t.lineDot}` }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: t.text,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 500 }}>{label}</span>
          {qtdItens > 0 && (
            <span style={{
              fontSize: 12,
              color: t.textMuted,
              background: t.bgInput,
              padding: '2px 8px',
              borderRadius: 4,
            }}>
              {qtdItens}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            fontSize: 14,
            fontWeight: 600,
            fontFamily: "'JetBrains Mono', monospace",
            color: total > 0 ? t.text : t.textMuted
          }}>
            {formatBRL(total)}
          </span>
          {isOpen ? <ChevronUp size={16} color={t.textMuted} /> : <ChevronDown size={16} color={t.textMuted} />}
        </div>
      </button>

      {isOpen && (
        <div style={{ paddingBottom: 16 }}>
          {items.map(item => {
            const sel = selecionados.find(s => String(s.id) === String(item.id));
            return (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  marginBottom: 4,
                  background: sel ? (darkMode ? '#3b82f610' : '#3b82f610') : t.bgInput,
                  borderRadius: 8,
                  border: sel ? `1px solid ${t.accent}40` : `1px solid transparent`,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    fontSize: 13,
                    color: t.text,
                    margin: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>{item.nome}</p>
                  <p style={{
                    fontSize: 12,
                    color: t.textMuted,
                    margin: 0,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>
                    {formatBRL(item.custo)}{showUnidade && item.unidade ? `/${item.unidade}` : ''}
                  </p>
                </div>
                {sel ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 12 }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); updateQtd(item.id, -1); }}
                      style={{
                        width: 28, height: 28, borderRadius: 6,
                        border: `1px solid ${t.border}`, background: t.bgCard,
                        color: t.text, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <Minus size={14} />
                    </button>
                    <span style={{ width: 24, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: t.text }}>{sel.qtd}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); updateQtd(item.id, 1); }}
                      style={{
                        width: 28, height: 28, borderRadius: 6,
                        border: `1px solid ${t.border}`, background: t.bgCard,
                        color: t.text, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); addItem(item); }}
                    style={{
                      width: 28, height: 28, borderRadius: 6,
                      border: `1px solid ${t.border}`, background: t.bgCard,
                      color: t.textMuted, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 12,
                    }}
                  >
                    <Plus size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Card do Canal - NOVO FLUXO
const CanalCard = ({ canal, resultado, isBest, darkMode, isMobile }) => {

  if (!canal.ativo) return null;

  const temDesconto = resultado.descontoPercent > 0;
  const partes = (canal.nome || '').split('·').map(s => s.trim());
  const marketplace = partes[0] || canal.nome;
  const tipo = partes[1] || '';
  const isShopee = marketplace.toLowerCase().includes('shopee');
  const accent = isShopee ? t.shopee : t.meli;
  const chipInk = isShopee ? '#fff' : t.meliInk;

  // Linha de dedução com guia pontilhada: label .......... valor
  const Ded = ({ label, value, strong }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', margin: '7px 0', fontFamily: t.mono, fontSize: 12.5, color: strong ? t.text : t.textSecondary, fontWeight: strong ? 700 : 400 }}>
      <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
      <span className="px-leader" />
      <span style={{ color: strong ? t.text : t.text }}>{formatBRL(value)}</span>
    </div>
  );

  return (
    <div className="px-receipt" style={{
      background: t.bgCard,
      border: `1.5px solid ${t.ink}`,
      padding: isMobile ? '16px 16px 22px' : '18px 18px 24px',
      boxShadow: isBest ? `0 0 0 3px ${accent}40` : 'none',
    }}>
      {/* faixa da marketplace */}
      <div style={{ height: 4, margin: isMobile ? '-16px -16px 14px' : '-18px -18px 14px', background: accent }} />

      {resultado.erro ? (
        <div style={{ padding: isMobile ? 14 : 24, textAlign: 'center', color: t.danger, fontFamily: t.mono, fontSize: 13 }}>{resultado.erro}</div>
      ) : (
        <>
          {/* cabeçalho do recibo */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <div style={{ fontFamily: t.disp, fontWeight: 700, fontSize: 15, letterSpacing: '-0.01em', color: t.text }}>{marketplace}</div>
              {tipo && <div style={{ fontFamily: t.mono, fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.textMuted, marginTop: 1 }}>{tipo}</div>}
            </div>
            {isBest && (
              <span style={{ fontFamily: t.mono, fontSize: 8.5, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700, padding: '4px 7px', border: `1.5px solid ${t.ink}`, background: accent, color: chipInk }}>
                melhor
              </span>
            )}
          </div>

          {/* PV projetado */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, paddingBottom: 14, marginBottom: 14, borderBottom: `1.5px dashed ${t.borderLight}` }}>
            <span style={{ fontFamily: t.mono, fontSize: 14, color: t.textMuted }}>R$</span>
            <span style={{ fontFamily: t.mono, fontWeight: 800, fontSize: isMobile ? 28 : 36, letterSpacing: '-0.03em', lineHeight: 1, color: t.text }}>
              {formatBRL(resultado.pvProjetado || resultado.precoVenda).replace('R$', '').trim()}
            </span>
            <span style={{ fontFamily: t.mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: t.textMuted, marginLeft: 'auto', textAlign: 'right', maxWidth: 72, lineHeight: 1.3 }}>
              preço de venda
            </span>
          </div>

          {/* desconto, se ativo */}
          {temDesconto && (
            <div style={{ display: 'flex', alignItems: 'baseline', margin: '0 0 12px', fontFamily: t.mono, fontSize: 12, color: t.textSecondary }}>
              <span>Desconto {formatPercent(resultado.descontoPercent)}</span>
              <span className="px-leader" />
              <span style={{ color: t.danger }}>−{formatBRL(resultado.descontoValor)}</span>
            </div>
          )}

          {/* deduções */}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: t.mono, fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.textMuted, margin: '4px 0 6px' }}>
            <span>Dedução</span><span>Valor</span>
          </div>
          <Ded label={`Comissão ${formatPercent(resultado.txVariavel)}`} value={resultado.txVariavelValor} />
          <Ded label={resultado.labelFixa || 'Frete'} value={resultado.txFixa} />
          <Ded label={`ADS ${formatPercent(resultado.ads)}`} value={resultado.adsValor} />
          <Ded label={`Imposto ${formatPercent(resultado.imposto)}`} value={resultado.impostoValor} />
          {resultado.txHolding > 0 && <Ded label="Holding" value={resultado.txHolding} />}
          <Ded label="Custo do anúncio" value={resultado.custoAnuncio} />

          {/* carimbo de margem */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, paddingTop: 14, borderTop: `1.5px dashed ${t.borderLight}` }}>
            <span style={{ fontFamily: t.mono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.textSecondary }}>Margem</span>
            <span className="px-seal" style={{ color: resultado.margem < 0 ? t.danger : t.profit, borderColor: resultado.margem < 0 ? t.danger : t.profit }}>
              <span style={{ fontSize: 18 }}>{formatBRL(resultado.margem)}</span>
              <span style={{ fontSize: 11 }}>{formatPercent(resultado.margemPercent)}</span>
            </span>
          </div>
        </>
      )}
      <div className="px-scallop" />
    </div>
  );
};

// Componente de Resumo do Anúncio
const ResumoAnuncio = ({ produtosSel, embalagensSel, insumosSel, darkMode, isMobile, onUpdateProdutos, onUpdateEmbalagens, onUpdateInsumos }) => {
  const totalItems = [...produtosSel, ...embalagensSel, ...insumosSel];

  if (totalItems.length === 0) return null;

  const totalGeral = totalItems.reduce((acc, s) => acc + (s.custo * s.qtd), 0);

  const Section = ({ title, items, color, onUpdate }) => {
    if (items.length === 0) return null;

    const updateQtd = (id, delta) => {
      if (!onUpdate) return;
      onUpdate(items.map(s => {
        if (String(s.id) === String(id)) {
          const newQtd = Math.max(1, s.qtd + delta);
          return { ...s, qtd: newQtd };
        }
        return s;
      }));
    };

    const removeItem = (id) => {
      if (!onUpdate) return;
      onUpdate(items.filter(s => String(s.id) !== String(id)));
    };

    return (
      <div style={{ marginBottom: 12, minWidth: 0 }}>
        <p style={{ fontFamily: t.mono, fontSize: 9.5, fontWeight: 700, color: color, textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.14em' }}>{title}</p>
        {items.map((item, idx) => (
          <div key={`${item.id}-${idx}`} style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 8px',
            fontSize: 13,
            background: darkMode ? '#ffffff05' : '#00000005',
            borderRadius: 6,
            marginBottom: 4
          }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{ color: t.textSecondary, fontWeight: 600, minWidth: '20px' }}>{item.qtd}x</span>
              <span style={{
                color: t.text,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }} title={item.nome}>{item.nome}</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', background: t.bgInput, borderRadius: 4, padding: '2px' }}>
                <button
                  onClick={() => updateQtd(item.id, -1)}
                  style={{
                    width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'none', border: 'none', color: t.textMuted, cursor: 'pointer', borderRadius: 3
                  }}
                  onMouseEnter={(e) => e.target.style.background = t.bgCard}
                  onMouseLeave={(e) => e.target.style.background = 'none'}
                >
                  <Minus size={12} />
                </button>
                <button
                  onClick={() => updateQtd(item.id, 1)}
                  style={{
                    width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'none', border: 'none', color: t.textMuted, cursor: 'pointer', borderRadius: 3
                  }}
                  onMouseEnter={(e) => e.target.style.background = t.bgCard}
                  onMouseLeave={(e) => e.target.style.background = 'none'}
                >
                  <Plus size={12} />
                </button>
              </div>

              <button
                onClick={() => removeItem(item.id)}
                style={{
                  width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'none', border: 'none', color: t.danger, opacity: 0.6, cursor: 'pointer', borderRadius: 4
                }}
                onMouseEnter={(e) => e.target.style.opacity = 1}
                onMouseLeave={(e) => e.target.style.opacity = 0.6}
              >
                <Trash2 size={14} />
              </button>

              <span style={{ fontFamily: "'JetBrains Mono', monospace", color: t.textSecondary, minWidth: 65, textAlign: 'right' }}>
                {formatBRL(item.custo * item.qtd)}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{
      background: t.bgCard,
      border: `1.5px solid ${t.ink}`,
      padding: 20,
      marginBottom: 24,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: `1.5px dashed ${t.borderLight}`, paddingBottom: 12 }}>
        <h3 style={{ fontFamily: t.mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.text, margin: 0 }}>Conteúdo do anúncio</h3>
        <span style={{ fontSize: 18, fontWeight: 800, color: t.text, fontFamily: t.mono }}>{formatBRL(totalGeral)}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24 }}>
        <Section title="Produtos" items={produtosSel} color={t.shopee} onUpdate={onUpdateProdutos} />
        <Section title="Embalagens" items={embalagensSel} color={t.profit} onUpdate={onUpdateEmbalagens} />
        <Section title="Insumos" items={insumosSel} color={t.textSecondary} onUpdate={onUpdateInsumos} />
      </div>
    </div>
  );
};

// Tabela de Simulação de Preço
const SimulacaoPreco = ({ resultados, custoAnuncio, canais, darkMode, isMobile }) => {

  // Colunas = resultados gerados (MELI Clássico, MELI Premium, Shopee).
  const cols = resultados.map(r => ({ id: r.id, nome: r.nome, params: r.params, base: r.resultado }));
  const variacoes = [-0.15, -0.10, -0.05, 0, 0.05, 0.10, 0.15];

  const simulacoes = variacoes.map(variacao => {
    const linha = { variacao };
    cols.forEach(canal => {
      if (!canal.base || canal.base.erro) { linha[canal.id] = { erro: true }; return; }
      const pvSimulado = canal.base.precoVenda * (1 + variacao);
      linha[canal.id] = resultadoNoPreco(canal.params, pvSimulado);
    });
    return linha;
  });

  return (
    <div style={{
      background: t.bgCard,
      border: `1.5px solid ${t.ink}`,
      overflow: 'hidden',
      marginTop: 24,
    }}>
      <div style={{ padding: '16px 20px', borderBottom: `1.5px dashed ${t.lineDot}` }}>
        <span style={{ fontFamily: t.mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.text }}>Simulação de preço</span>
        <p style={{ fontFamily: t.mono, fontSize: 10, color: t.textMuted, margin: '4px 0 0 0', letterSpacing: '0.04em' }}>
          como a margem varia conforme o preço
        </p>
      </div>

      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 12 }}>
          {simulacoes.map((linha, idx) => {
            const isAtual = linha.variacao === 0;
            return (
              <div
                key={idx}
                style={{
                  background: isAtual ? (darkMode ? '#3b82f615' : '#eff6ff') : t.bgInput,
                  borderRadius: 12, border: `1px solid ${isAtual ? t.accent + '40' : t.border}`,
                  padding: 16
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, pb: 8, borderBottom: `1px solid ${t.border}20` }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: isAtual ? t.accent : t.textMuted, textTransform: 'uppercase' }}>
                    {linha.variacao === 0 ? 'Cenário Atual' : `Variação de ${linha.variacao > 0 ? '+' : ''}${(linha.variacao * 100).toFixed(0)}%`}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {cols.map(canal => {
                    const sim = linha[canal.id];
                    if (sim?.erro) return null;
                    return (
                      <div key={canal.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: t.textSecondary, fontWeight: 500 }}>{canal.nome}</span>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: t.text, fontFamily: "'JetBrains Mono', monospace" }}>{formatBRL(sim.precoVenda)}</div>
                          <div style={{ fontSize: 11, color: sim.margem > 0 ? t.success : t.danger, fontFamily: "'JetBrains Mono', monospace" }}>
                            {formatBRL(sim.margem)} ({formatPercent(sim.margemPercent)})
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: t.bgInput }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: t.textMuted, fontSize: 11, textTransform: 'uppercase' }}>
                  Variação
                </th>
                {cols.map(canal => (
                  <th key={canal.id} colSpan={2} style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: t.textMuted, fontSize: 11, textTransform: 'uppercase', borderLeft: `1px solid ${t.border}` }}>
                    {canal.nome}
                  </th>
                ))}
              </tr>
              <tr style={{ background: t.bgInput }}>
                <th style={{ padding: '8px 16px' }}></th>
                {cols.map(canal => (
                  <React.Fragment key={canal.id}>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500, color: t.textMuted, fontSize: 11, borderLeft: `1px solid ${t.border}` }}>Preço</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500, color: t.textMuted, fontSize: 11 }}>Margem</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {simulacoes.map((linha, idx) => {
                const isAtual = linha.variacao === 0;
                return (
                  <tr
                    key={idx}
                    style={{
                      background: isAtual ? (darkMode ? '#3b82f615' : '#eff6ff') : 'transparent',
                      borderTop: `1px solid ${t.border}`,
                    }}
                  >
                    <td style={{
                      padding: '10px 16px',
                      fontWeight: isAtual ? 600 : 400,
                      color: isAtual ? t.accent : t.textSecondary,
                    }}>
                      {linha.variacao === 0 ? 'Atual' : `${linha.variacao > 0 ? '+' : ''}${(linha.variacao * 100).toFixed(0)}%`}
                    </td>
                    {cols.map(canal => {
                      const sim = linha[canal.id];
                      if (sim?.erro) {
                        return (
                          <React.Fragment key={canal.id}>
                            <td colSpan={2} style={{ padding: '10px 12px', textAlign: 'center', color: t.textMuted, borderLeft: `1px solid ${t.border}` }}>—</td>
                          </React.Fragment>
                        );
                      }
                      return (
                        <React.Fragment key={canal.id}>
                          <td style={{
                            padding: '10px 12px',
                            textAlign: 'right',
                            fontFamily: "'JetBrains Mono', monospace",
                            fontWeight: isAtual ? 600 : 400,
                            color: isAtual ? t.text : t.textSecondary,
                            borderLeft: `1px solid ${t.border}`,
                          }}>
                            {formatBRL(sim.precoVenda)}
                          </td>
                          <td style={{
                            padding: '10px 12px',
                            textAlign: 'right',
                            fontFamily: "'JetBrains Mono', monospace",
                            fontWeight: isAtual ? 600 : 400,
                            color: sim.margem > 0 ? t.success : t.danger,
                          }}>
                            {formatBRL(sim.margem)} <span style={{ opacity: 0.7, fontSize: 11 }}>({formatPercent(sim.margemPercent)})</span>
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Modal
const Modal = ({ isOpen, onClose, title, children, darkMode }) => {
  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: '100%', maxWidth: 480, background: t.bgCard, borderRadius: 2, border: `1.5px solid ${t.ink}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: `1.5px dashed ${t.lineDot}` }}>
          <span style={{ fontFamily: t.mono, fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: t.text }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 4 }}><X size={20} /></button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
};

// Admin Table com XLSX e Validação de SKU
const AdminTable = ({ items, setItems, columns, title, darkMode, isMobile }) => {
  const [editingItem, setEditingItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [skuError, setSkuError] = useState('');
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState({ duplicates: [], newItems: [] });
  const fileInputRef = useRef(null);

  const handleEdit = (item) => {
    setFormData(item);
    setEditingItem(item.id);
    setSkuError('');
    setShowModal(true);
  };

  const handleNew = () => {
    setFormData({});
    setEditingItem(null);
    setSkuError('');
    setShowModal(true);
  };

  // Gerador de ID único e robusto
  const generateId = () => `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Validação de SKU
  const validateSku = (sku, currentItemId = null) => {
    if (!sku || sku.trim() === '') {
      return 'SKU é obrigatório';
    }

    const skuExists = items.some(item =>
      item.sku?.toLowerCase() === sku.toLowerCase() && item.id !== currentItemId
    );

    if (skuExists) {
      return 'SKU já existe no cadastro';
    }

    return '';
  };

  const handleSave = async () => {
    // Validar SKU
    const error = validateSku(formData.sku, editingItem);
    if (error) {
      setSkuError(error);
      return;
    }

    let savedItem;
    if (editingItem) {
      savedItem = { ...formData, id: editingItem };
      setItems(items.map(item => item.id === editingItem ? savedItem : item));
    } else {
      savedItem = { ...formData, id: generateId() };
      setItems([...items, savedItem]);
    }

    // Persistência no PocketBase
    if (title.toLowerCase() === 'produtos') saveItem('prexo_produtos', savedItem);
    if (title.toLowerCase() === 'embalagens') saveItem('prexo_embalagens', savedItem);
    if (title.toLowerCase() === 'insumos') saveItem('prexo_insumos', savedItem);

    setShowModal(false);
    setFormData({});
    setEditingItem(null);
    setSkuError('');
  };

  const handleDelete = (id) => {
    if (window.confirm('Excluir este item?')) {
      const itemToDelete = items.find(i => i.id === id);
      setItems(items.filter(item => item.id !== id));

      // Persistência no PocketBase
      if (itemToDelete && typeof itemToDelete.id === 'string') {
        const coll = title.toLowerCase() === 'produtos' ? 'prexo_produtos' :
          title.toLowerCase() === 'embalagens' ? 'prexo_embalagens' : 'prexo_insumos';
        deleteItem(coll, itemToDelete.id);
      }
    }
  };

  // XLSX Export
  const handleExport = () => {
    const data = items.map(item => {
      const row = { 'Tipo': title }; // Adiciona tipo como primeira coluna
      columns.forEach(col => {
        row[col.label] = col.type === 'number' ? item[col.key] : (item[col.key] || '');
      });
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title);

    const colWidths = [{ wch: 15 }, ...columns.map(col => ({ wch: Math.max(col.label.length, 20) }))];
    ws['!cols'] = colWidths;

    XLSX.writeFile(wb, `${title}.xlsx`);
  };

  // XLSX Import com validação de SKU e Tipo
  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(sheet);

      if (json.length === 0) {
        alert('Erro: Arquivo vazio.');
        e.target.value = '';
        return;
      }

      // Verificar se tem coluna Tipo
      if (!json[0].hasOwnProperty('Tipo')) {
        alert('Erro: Coluna "Tipo" não encontrada no arquivo.\n\nUse o template correto para importação.');
        e.target.value = '';
        return;
      }

      // Verificar se o Tipo corresponde à tabela atual
      const tipoArquivo = json[0].Tipo;
      if (tipoArquivo !== title) {
        alert(`Erro: Este arquivo é de "${tipoArquivo}", mas você está tentando importar em "${title}".\n\nUse o arquivo correto ou importe na aba correta.`);
        e.target.value = '';
        return;
      }

      // Verificar se todos os registros têm o mesmo tipo
      const tiposInvalidos = json.filter(row => row.Tipo !== title);
      if (tiposInvalidos.length > 0) {
        alert(`Erro: O arquivo contém ${tiposInvalidos.length} registro(s) com tipo diferente de "${title}".`);
        e.target.value = '';
        return;
      }

      // Verificar se tem coluna SKU
      if (!json[0].hasOwnProperty('SKU')) {
        alert('Erro: Coluna "SKU" não encontrada no arquivo.');
        e.target.value = '';
        return;
      }

      // Verificar duplicados internos no arquivo
      const skusNoArquivo = json.map(row => row.SKU?.toString().toLowerCase()).filter(Boolean);
      const skusDuplicadosInternos = skusNoArquivo.filter((sku, index) =>
        skusNoArquivo.indexOf(sku) !== index
      );

      if (skusDuplicadosInternos.length > 0) {
        alert(`Erro: O arquivo contém SKUs duplicados internamente: ${[...new Set(skusDuplicadosInternos)].join(', ')}`);
        e.target.value = '';
        return;
      }

      // Mapear items do arquivo (ignorando coluna Tipo)
      const importedItems = json.map((row) => {
        const item = {};
        columns.forEach(col => {
          const value = row[col.label];
          item[col.key] = col.type === 'number' ? (parseFloat(value) || 0) : (value || '');
        });
        return item;
      });

      // Separar novos vs duplicados
      const newItems = [];
      const duplicates = [];

      importedItems.forEach(importedItem => {
        const existingItem = items.find(item =>
          item.sku?.toLowerCase() === importedItem.sku?.toLowerCase()
        );

        if (existingItem) {
          duplicates.push({
            existing: existingItem,
            imported: importedItem
          });
        } else {
          newItems.push(importedItem);
        }
      });

      // Se tem duplicados, mostrar modal
      if (duplicates.length > 0) {
        setConflictData({ duplicates, newItems });
        setShowConflictModal(true);
      } else {
        // Só novos, inserir direto
        const itemsComId = newItems.map((item) => ({
          ...item,
          id: generateId()
        }));

        const updatedList = [...items, ...itemsComId];
        setItems(updatedList);

        // Salvar novos no PB se logado
        const coll = title.toLowerCase() === 'produtos' ? 'prexo_produtos' :
          title.toLowerCase() === 'embalagens' ? 'prexo_embalagens' : 'prexo_insumos';

        itemsComId.forEach(it => saveItem(coll, it));

        alert(`${itemsComId.length} item(s) importado(s) com sucesso!`);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  // Ações do modal de conflito
  const handleConflictAction = (action) => {
    const coll = title.toLowerCase() === 'produtos' ? 'prexo_produtos' :
      title.toLowerCase() === 'embalagens' ? 'prexo_embalagens' : 'prexo_insumos';

    if (action === 'update') {
      // Atualizar existentes + inserir novos
      const updatedItems = items.map(item => {
        const duplicate = duplicates.find(d => d.existing.id === item.id);
        if (duplicate) {
          const updated = { ...duplicate.imported, id: item.id };
          saveItem(coll, updated);
          return updated;
        }
        return item;
      });

      const newItemsComId = newItems.map((item) => ({
        ...item,
        id: generateId()
      }));

      newItemsComId.forEach(it => saveItem(coll, it));

      setItems([...updatedItems, ...newItemsComId]);
      alert(`${duplicates.length} item(s) atualizado(s) e ${newItems.length} novo(s) inserido(s).`);

    } else if (action === 'ignore') {
      // Só inserir novos, ignorar duplicados
      const newItemsComId = newItems.map((item) => ({
        ...item,
        id: generateId()
      }));

      newItemsComId.forEach(it => saveItem(coll, it));

      setItems([...items, ...newItemsComId]);
      alert(`${newItems.length} item(s) novo(s) inserido(s). ${duplicates.length} duplicado(s) ignorado(s).`);
    }

    setShowConflictModal(false);
    setConflictData({ duplicates: [], newItems: [] });
  };

  // Download Template
  const handleDownloadTemplate = () => {
    const exampleData = [{
      'Tipo': title,
      'Nome': `Exemplo de ${title.slice(0, -1)}`, // Remove 's' do plural
      'SKU': 'EX-001',
      'Custo': 10.50,
      'Unidade': 'un'
    }];

    const instructions = [
      { 'Instruções': 'Como usar este template:' },
      { 'Instruções': '' },
      { 'Instruções': `1. Este template é para: ${title.toUpperCase()}` },
      { 'Instruções': '2. NÃO altere a coluna "Tipo" - ela identifica o tipo de cadastro' },
      { 'Instruções': '3. Preencha os dados na aba "Dados"' },
      { 'Instruções': '4. Nome: Nome completo do item' },
      { 'Instruções': '5. SKU: Código único do item (OBRIGATÓRIO, não pode repetir)' },
      { 'Instruções': '6. Custo: Valor numérico (ex: 10.50)' },
      { 'Instruções': '7. Unidade: un, m, kg, folha, etc.' },
      { 'Instruções': '8. Salve o arquivo e importe no sistema' },
      { 'Instruções': '' },
      { 'Instruções': 'IMPORTANTE:' },
      { 'Instruções': '- SKUs duplicados serão identificados na importação' },
      { 'Instruções': `- Este arquivo só pode ser importado em "${title}"` },
    ];

    const wb = XLSX.utils.book_new();

    const wsData = XLSX.utils.json_to_sheet(exampleData);
    wsData['!cols'] = [{ wch: 15 }, { wch: 30 }, { wch: 15 }, { wch: 10 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, wsData, 'Dados');

    const wsInst = XLSX.utils.json_to_sheet(instructions);
    wsInst['!cols'] = [{ wch: 60 }];
    XLSX.utils.book_append_sheet(wb, wsInst, 'Instruções');

    XLSX.writeFile(wb, `Template_${title}.xlsx`);
  };

  return (
    <div style={{ background: t.bgCard, border: `1.5px solid ${t.ink}` }}>
      <div style={{
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        padding: 18,
        borderBottom: `1.5px dashed ${t.lineDot}`,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 12 : 0
      }}>
        <span style={{ fontFamily: t.mono, fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.text }}>{title}</span>
        <div style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          width: isMobile ? '100%' : 'auto'
        }}>
          <input type="file" ref={fileInputRef} onChange={handleImport} accept=".xlsx,.xls" style={{ display: 'none' }} />
          <button onClick={handleDownloadTemplate} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
            background: t.bgCard, border: `1.5px solid ${t.ink}`, borderRadius: 0,
            color: t.text, fontFamily: t.mono, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer',
          }}>
            <FileSpreadsheet size={14} /> Template
          </button>
          <button onClick={() => fileInputRef.current?.click()} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
            background: t.bgCard, border: `1.5px solid ${t.ink}`, borderRadius: 0,
            color: t.text, fontFamily: t.mono, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer',
          }}>
            <Upload size={14} /> Importar
          </button>
          <button onClick={handleExport} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
            background: t.bgCard, border: `1.5px solid ${t.ink}`, borderRadius: 0,
            color: t.text, fontFamily: t.mono, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer',
          }}>
            <Download size={14} /> Exportar
          </button>
          <button onClick={handleNew} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
            background: t.ink, border: 'none', borderRadius: 0,
            color: t.bg, fontFamily: t.mono, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontWeight: 700,
          }}>
            <Plus size={14} /> Novo
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto', width: '100%' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? 600 : 'auto' }}>
          <thead>
            <tr style={{ background: t.bgInput, borderBottom: `1.5px solid ${t.ink}` }}>
              {columns.map(col => (
                <th key={col.key} style={{ padding: '12px 16px', textAlign: 'left', fontFamily: t.mono, fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                  {col.label}
                </th>
              ))}
              <th style={{ padding: '12px 16px', textAlign: 'right', fontFamily: t.mono, fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} style={{ borderTop: `1.5px dashed ${t.lineDot}` }}>
                {columns.map(col => (
                  <td key={col.key} style={{ padding: '12px 16px', fontSize: 14, color: t.text, fontFamily: col.type === 'number' ? t.mono : 'inherit' }}>
                    {col.type === 'number' ? formatBRL(item[col.key]) : item[col.key]}
                  </td>
                ))}
                <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                  <button onClick={() => handleEdit(item)} style={{ background: 'none', border: 'none', color: t.accent, cursor: 'pointer', padding: 4, marginRight: 4 }}><Edit2 size={14} /></button>
                  <button onClick={() => handleDelete(item.id)} style={{ background: 'none', border: 'none', color: t.danger, cursor: 'pointer', padding: 4 }}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal de Edição/Novo */}
      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setSkuError(''); }} title={editingItem ? 'Editar' : 'Novo'} darkMode={darkMode}>
        {columns.map(col => (
          <div key={col.key} style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontFamily: t.mono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: t.textMuted, marginBottom: 6 }}>
              {col.label} {col.key === 'sku' && <span style={{ color: t.danger }}>*</span>}
            </label>
            <input
              type={col.type === 'number' ? 'text' : 'text'}
              value={col.type === 'number' ? String(formData[col.key] || '').replace('.', ',') : (formData[col.key] || '')}
              onChange={(e) => {
                const val = col.type === 'number' ? parseFloat(e.target.value.replace(',', '.')) : e.target.value;
                setFormData({ ...formData, [col.key]: val });
                if (col.key === 'sku') {
                  setSkuError(validateSku(e.target.value, editingItem));
                }
              }}
              style={{
                width: '100%', padding: 11, borderRadius: 0,
                border: `1.5px solid ${col.key === 'sku' && skuError ? t.danger : t.ink}`,
                background: t.bgInput, color: t.text, fontSize: 14, fontFamily: col.type === 'number' ? t.mono : 'inherit', boxSizing: 'border-box',
              }}
            />
            {col.key === 'sku' && skuError && (
              <p style={{ fontSize: 12, color: t.danger, margin: '4px 0 0 0' }}>{skuError}</p>
            )}
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button onClick={() => { setShowModal(false); setSkuError(''); }} style={{ padding: '10px 16px', borderRadius: 0, border: `1.5px solid ${t.ink}`, background: t.bgCard, color: t.text, cursor: 'pointer', fontFamily: t.mono, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600 }}>Cancelar</button>
          <button onClick={handleSave} disabled={!!skuError} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 0, border: 'none',
            background: skuError ? t.textMuted : t.ink,
            color: t.bg, cursor: skuError ? 'not-allowed' : 'pointer', fontFamily: t.mono, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700
          }}><Save size={14} /> Salvar</button>
        </div>
      </Modal>

      {/* Modal de Conflito de Importação */}
      {showConflictModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={() => setShowConflictModal(false)} />
          <div style={{ position: 'relative', width: '100%', maxWidth: 600, background: t.bgCard, borderRadius: 2, border: `1.5px solid ${t.ink}`, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 20, borderBottom: `1.5px dashed ${t.lineDot}` }}>
              <AlertTriangle size={24} color={t.danger} />
              <div>
                <span style={{ fontFamily: t.disp, fontSize: 16, fontWeight: 700, color: t.text, display: 'block' }}>SKUs Duplicados Encontrados</span>
                <span style={{ fontSize: 13, color: t.textMuted }}>
                  {conflictData.duplicates.length} SKU(s) já existe(m) no cadastro
                </span>
              </div>
            </div>

            <div style={{ padding: 20, overflow: 'auto', flex: 1 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: t.bgInput }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: t.textMuted }}>SKU</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: t.textMuted }}>Atual</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: t.textMuted }}>Importação</th>
                  </tr>
                </thead>
                <tbody>
                  {conflictData.duplicates.map((dup, idx) => (
                    <tr key={idx} style={{ borderTop: `1px solid ${t.border}` }}>
                      <td style={{ padding: '10px 12px', color: t.text, fontFamily: "'JetBrains Mono', monospace" }}>{dup.existing.sku}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: t.textSecondary }}>{formatBRL(dup.existing.custo)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: t.accent, fontWeight: 600 }}>{formatBRL(dup.imported.custo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {conflictData.newItems.length > 0 && (
                <p style={{ fontSize: 13, color: t.textSecondary, margin: '16px 0 0 0' }}>
                  + {conflictData.newItems.length} item(s) novo(s) serão inseridos.
                </p>
              )}
            </div>

            <div style={{ padding: 20, borderTop: `1px solid ${t.border}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowConflictModal(false)} style={{
                padding: '10px 16px', borderRadius: 6, border: `1px solid ${t.border}`,
                background: t.bgInput, color: t.textSecondary, cursor: 'pointer', fontSize: 14
              }}>
                Cancelar
              </button>
              <button onClick={() => handleConflictAction('ignore')} style={{
                padding: '10px 16px', borderRadius: 6, border: `1px solid ${t.border}`,
                background: t.bgInput, color: t.text, cursor: 'pointer', fontSize: 14
              }}>
                Ignorar duplicados
              </button>
              <button onClick={() => handleConflictAction('update')} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 6,
                border: 'none', background: t.accent, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500
              }}>
                Atualizar existentes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Canais
const CanaisAdmin = ({ canais, setCanais, darkMode, isMobile }) => {
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});

  const handleSave = (id) => {
    const updatedCanal = { ...canais[id], ...formData };
    setCanais({ ...canais, [id]: updatedCanal });
    saveItem('prexo_canais', updatedCanal);
    setEditingId(null); setFormData({});
  };

  const handleToggleAtivo = (id, isActive) => {
    const updatedCanal = { ...canais[id], ativo: isActive };
    setCanais({ ...canais, [id]: updatedCanal });
    saveItem('prexo_canais', updatedCanal);
  };

  const InputField = ({ label, value, onChange }) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, color: t.textMuted, marginBottom: 4 }}>{label}</label>
      <input
        type="text"
        value={String(value || '').replace('.', ',')}
        onChange={(e) => onChange(parseFloat(e.target.value.replace(',', '.')) || 0)}
        style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text, fontSize: 13, boxSizing: 'border-box' }}
      />
    </div>
  );

  return (
    <div style={{ background: t.bgCard, borderRadius: 12, border: `1px solid ${t.border}` }}>
      <div style={{ padding: 20, borderBottom: `1px solid ${t.border}` }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: t.text }}>Configuração de Canais</span>
      </div>

      {Object.values(canais).map(canal => (
        <div key={canal.id} style={{ padding: 20, borderBottom: `1px solid ${t.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{canal.nome}</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: t.textMuted }}>
                <input type="checkbox" checked={canal.ativo} onChange={(e) => handleToggleAtivo(canal.id, e.target.checked)} />
                Ativo
              </label>
            </div>
            {editingId === canal.id ? (
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => handleSave(canal.id)} style={{ padding: 6, borderRadius: 4, border: 'none', background: t.success, color: '#fff', cursor: 'pointer' }}><Save size={14} /></button>
                <button onClick={() => setEditingId(null)} style={{ padding: 6, borderRadius: 4, border: 'none', background: t.bgInput, color: t.textMuted, cursor: 'pointer' }}><X size={14} /></button>
              </div>
            ) : (
              <button onClick={() => { setEditingId(canal.id); setFormData(canal); }} style={{ padding: 6, borderRadius: 4, border: 'none', background: t.bgInput, color: t.accent, cursor: 'pointer' }}><Edit2 size={14} /></button>
            )}
          </div>

          {editingId === canal.id ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              <InputField label="TX Holding (R$)" value={formData.txHolding} onChange={(v) => setFormData({ ...formData, txHolding: v })} />
              {canal.id === 'meli' ? (
                <>
                  <InputField label="TX Fixa (< limiar)" value={formData.txFixaBaixa} onChange={(v) => setFormData({ ...formData, txFixaBaixa: v })} />
                  <InputField label="TX Fixa (≥ limiar)" value={formData.txFixaAlta} onChange={(v) => setFormData({ ...formData, txFixaAlta: v })} />
                  <InputField label="Limiar (R$)" value={formData.limiarTxFixa} onChange={(v) => setFormData({ ...formData, limiarTxFixa: v })} />
                </>
              ) : (
                <InputField label="TX Fixa (R$)" value={formData.txFixa} onChange={(v) => setFormData({ ...formData, txFixa: v })} />
              )}
              <InputField label="TX Variável (%)" value={(formData.txVariavel || 0) * 100} onChange={(v) => setFormData({ ...formData, txVariavel: v / 100 })} />
              <InputField label="ADS (%)" value={(formData.ads || 0) * 100} onChange={(v) => setFormData({ ...formData, ads: v / 100 })} />
              <InputField label="Imposto (%)" value={(formData.imposto || 0) * 100} onChange={(v) => setFormData({ ...formData, imposto: v / 100 })} />
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 16, fontSize: 13 }}>
              <div><span style={{ color: t.textMuted, display: 'block', fontSize: 11 }}>Holding</span><span style={{ color: t.text, fontFamily: "'JetBrains Mono', monospace" }}>{formatBRL(canal.txHolding)}</span></div>
              <div><span style={{ color: t.textMuted, display: 'block', fontSize: 11 }}>TX Fixa</span><span style={{ color: t.text, fontFamily: "'JetBrains Mono', monospace" }}>{canal.id === 'meli' ? `${formatBRL(canal.txFixaBaixa)} / ${formatBRL(canal.txFixaAlta)}` : formatBRL(canal.txFixa)}</span></div>
              <div><span style={{ color: t.textMuted, display: 'block', fontSize: 11 }}>Variável</span><span style={{ color: t.text, fontFamily: "'JetBrains Mono', monospace" }}>{formatPercent(canal.txVariavel)}</span></div>
              <div><span style={{ color: t.textMuted, display: 'block', fontSize: 11 }}>ADS</span><span style={{ color: t.text, fontFamily: "'JetBrains Mono', monospace" }}>{formatPercent(canal.ads)}</span></div>
              <div><span style={{ color: t.textMuted, display: 'block', fontSize: 11 }}>Imposto</span><span style={{ color: t.text, fontFamily: "'JetBrains Mono', monospace" }}>{formatPercent(canal.imposto)}</span></div>
              {canal.id === 'meli' && <div><span style={{ color: t.textMuted, display: 'block', fontSize: 11 }}>Limiar</span><span style={{ color: t.text, fontFamily: "'JetBrains Mono', monospace" }}>{formatBRL(canal.limiarTxFixa)}</span></div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ==================== PERSISTÊNCIA LOCAL ====================
const STORAGE_KEYS = {
  produtos: 'precificacao_produtos',
  embalagens: 'precificacao_embalagens',
  insumos: 'precificacao_insumos',
  canais: 'precificacao_canais',
  darkMode: 'precificacao_darkMode',
  activeView: 'precificacao_activeView',
  activeAdmin: 'precificacao_activeAdmin',
};

const loadFromStorage = (key, defaultValue) => {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Recuperação crítica: se for o objeto de canais e estiver vazio, usa o padrão
      if (key === STORAGE_KEYS.canais && (!parsed || Object.keys(parsed).length === 0)) {
        return defaultValue;
      }
      return parsed;
    }
  } catch (e) {
    console.error('Erro ao ler localStorage:', e);
  }
  return defaultValue;
};

const saveToStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error('Erro ao salvar localStorage:', e);
  }
};

// ==================== APP PRINCIPAL ====================
export default function App() {
  // Estados de Autenticação
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Bootstrap de autenticação:
  // 1) escuta mudanças de sessão do Supabase
  // 2) tenta auto-login pela "portaria única" (Cloudflare Access -> /api/session)
  //    Se a ponte falhar (ex.: rodando fora do Access), cai no login do app.
  useEffect(() => {
    let mounted = true;
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        if (mounted) { setUser(null); setAuthLoading(false); }
        return;
      }
      fetchCurrentUser().then((u) => {
        if (mounted) { setUser(u); setAuthLoading(false); }
      });
    });

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) return; // já logado (onAuthStateChange resolve)
        const r = await fetch('/api/session', { credentials: 'include' });
        if (r.ok) {
          const { access_token, refresh_token } = await r.json();
          if (access_token && refresh_token) {
            await supabase.auth.setSession({ access_token, refresh_token });
            return; // onAuthStateChange dispara com a nova sessão
          }
        }
      } catch (e) { /* ponte indisponível — usa o login do app como fallback */ }
      if (mounted) setAuthLoading(false);
    })();

    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  // Carregar do localStorage ou usar valores iniciais
  const [darkMode, setDarkMode] = useState(() => loadFromStorage(STORAGE_KEYS.darkMode, true));
  const [activeView, setActiveView] = useState(() => loadFromStorage(STORAGE_KEYS.activeView, 'calculadora'));
  const [activeAdmin, setActiveAdmin] = useState(() => loadFromStorage(STORAGE_KEYS.activeAdmin, 'produtos'));
  const [modoCalculadora, setModoCalculadora] = useState('precificar'); // 'precificar' ou 'raiox'

  // Estados do Raio-X
  const [raioXPV, setRaioXPV] = useState('');
  const [raioXCusto, setRaioXCusto] = useState('');
  const [raioXCanal, setRaioXCanal] = useState('ambos'); // 'ambos', 'meli', 'shopee'
  const [showExportMenuRaioX, setShowExportMenuRaioX] = useState(false);
  const [showExportMenuCalc, setShowExportMenuCalc] = useState(false);
  const raioXResultRef = useRef(null);
  const calculadoraResultRef = useRef(null);

  const [produtos, setProdutos] = useState(() => loadFromStorage(STORAGE_KEYS.produtos, PRODUTOS_INICIAL));
  const [embalagens, setEmbalagens] = useState(() => loadFromStorage(STORAGE_KEYS.embalagens, EMBALAGENS_INICIAL));
  const [insumos, setInsumos] = useState(() => loadFromStorage(STORAGE_KEYS.insumos, INSUMOS_INICIAL));
  const [canais, setCanais] = useState(() => loadFromStorage(STORAGE_KEYS.canais, CANAIS_INICIAL));

  const [produtosSel, setProdutosSel] = useState([]);
  const [embalagensSel, setEmbalagensSel] = useState([]);
  const [insumosSel, setInsumosSel] = useState([]);

  const [modoMargem, setModoMargem] = useState('percentual');
  const [margemAlvo, setMargemAlvo] = useState(0.35);
  const [margemAlvoReais, setMargemAlvoReais] = useState(10);

  // Mercado Livre Full (armazenagem) — desligado por padrão; reputação verde fixa.
  const [fullAtivo, setFullAtivo] = useState(false);
  const [fullDias, setFullDias] = useState(30);

  const [descontoAtivo, setDescontoAtivo] = useState(false);
  const [desconto, setDesconto] = useState(0.10);
  const [modoDesconto, setModoDesconto] = useState('direto');
  const [aplicaDescontoEm, setAplicaDescontoEm] = useState('todos');

  // Salvar no localStorage quando os dados mudarem
  useEffect(() => { saveToStorage(STORAGE_KEYS.produtos, produtos); }, [produtos]);
  useEffect(() => { saveToStorage(STORAGE_KEYS.embalagens, embalagens); }, [embalagens]);
  useEffect(() => { saveToStorage(STORAGE_KEYS.insumos, insumos); }, [insumos]);
  useEffect(() => { saveToStorage(STORAGE_KEYS.canais, canais); }, [canais]);
  useEffect(() => { saveToStorage(STORAGE_KEYS.darkMode, darkMode); }, [darkMode]);
  useEffect(() => { saveToStorage(STORAGE_KEYS.activeView, activeView); }, [activeView]);
  useEffect(() => { saveToStorage(STORAGE_KEYS.activeAdmin, activeAdmin); }, [activeAdmin]);

  // Sync theme with DOM
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Efeito para redirecionar se o usuário estiver em uma aba restrita no mobile
  useEffect(() => {
    if (isMobile && (activeView === 'cadastros' || activeView === 'admin')) {
      setActiveView('calculadora');
    }
  }, [isMobile, activeView]);

  // Sincronizar com PocketBase quando o usuário logar
  useEffect(() => {
    if (user) {
      const loadFromPB = async () => {
        const pbProdutos = await syncCollection('prexo_produtos', produtos);
        if (pbProdutos) setProdutos(pbProdutos);

        const pbEmbalagens = await syncCollection('prexo_embalagens', embalagens);
        if (pbEmbalagens) setEmbalagens(pbEmbalagens);

        const pbInsumos = await syncCollection('prexo_insumos', insumos);
        if (pbInsumos) setInsumos(pbInsumos);

        const pbCanais = await syncCollection('prexo_canais', canais);
        if (pbCanais && pbCanais.length > 0) {
          const canaisObj = {};
          // Mapear de volta para as chaves 'meli' e 'shopee' baseando-se no nome ou identificador
          pbCanais.forEach(c => {
            const lowerNome = (c.nome || '').toLowerCase();
            const id = c.id_origem || c.id;
            if (lowerNome.includes('mercado') || lowerNome.includes('meli') || id === 'meli') {
              canaisObj['meli'] = c;
            } else if (lowerNome.includes('shopee') || id === 'shopee') {
              canaisObj['shopee'] = c;
            } else {
              canaisObj[c.id] = c;
            }
          });
          setCanais(prev => ({ ...prev, ...canaisObj }));
        } else {
          // Se a nuvem estiver vazia, garantimos que pelo menos os padrões existam localmente
          setCanais(prev => {
            if (!prev || Object.keys(prev).length === 0) return CANAIS_INICIAL;
            return prev;
          });
        }
      };
      loadFromPB();
    }
  }, [user]);

  // Otimização: A persistência agora é feita de forma explícita nos componentes Admin

  const handleLimpar = () => {
    setProdutosSel([]);
    setEmbalagensSel([]);
    setInsumosSel([]);
  };

  // Funções de Exportação
  const getExportFileName = (prefix, extension) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    return `${prefix}_${timestamp}.${extension}`;
  };

  const exportarRaioXExcel = () => {
    const pv = parseFloat(raioXPV.replace(',', '.')) || 0;
    const custo = parseFloat(raioXCusto.replace(',', '.')) || 0;
    if (pv <= 0) return alert('Informe o preço de venda antes de exportar.');

    const mC = canais.meli, sC = canais.shopee;
    const baseMeli = {
      canal: 'meli', custoAnuncio: custo,
      holding: mC?.txHolding ?? 2, adsPct: (mC?.ads ?? 0.03) * 100, impostoPct: (mC?.imposto ?? 0.10) * 100,
      comissaoClassico: 12, comissaoPremium: 16.5, reputacao: 'green',
      pesoRealKg: dadosEnvio.pesoRealKg, pesoTarifadoKg: dadosEnvio.pesoTarifadoKg,
      comp: dadosEnvio.comp, larg: dadosEnvio.larg, alt: dadosEnvio.alt,
      full: fullAtivo, dias: fullDias, ofereceGratis: false
    };
    const linhas = [];
    if ((raioXCanal === 'ambos' || raioXCanal === 'meli') && mC?.ativo) {
      linhas.push(['Mercado Livre · Clássico', resultadoNoPreco({ ...baseMeli, tipoAnuncio: 'classico' }, pv)]);
      linhas.push(['Mercado Livre · Premium', resultadoNoPreco({ ...baseMeli, tipoAnuncio: 'premium' }, pv)]);
    }
    if ((raioXCanal === 'ambos' || raioXCanal === 'shopee') && sC?.ativo) {
      linhas.push(['Shopee', resultadoNoPreco({ canal: 'shopee', custoAnuncio: custo, holding: sC?.txHolding ?? 2, adsPct: (sC?.ads ?? 0.03) * 100, impostoPct: (sC?.imposto ?? 0.10) * 100 }, pv)]);
    }
    const dados = linhas.map(([nome, res]) => ({
      'Canal': nome,
      'Preço de Venda': pv,
      'Custo': custo,
      'TX Holding': res.txHolding,
      'Frete / Taxa fixa': res.txFixa,
      'Comissão (%)': (res.txVariavel * 100).toFixed(1) + '%',
      'Comissão (R$)': res.txVariavelValor,
      'ADS (%)': (res.ads * 100).toFixed(1) + '%',
      'ADS (R$)': res.adsValor,
      'Imposto (%)': (res.imposto * 100).toFixed(1) + '%',
      'Imposto (R$)': res.impostoValor,
      'CAV Total': res.cav,
      'Recebe': res.recebe,
      'Margem (R$)': res.margem,
      'Margem (%)': (res.margemPercent * 100).toFixed(2) + '%',
      'Status': res.saudeTexto
    }));

    const ws = XLSX.utils.json_to_sheet(dados);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Raio-X');
    ws['!cols'] = Object.keys(dados[0] || {}).map(() => ({ wch: 15 }));
    XLSX.writeFile(wb, getExportFileName('RaioX', 'xlsx'));
    setShowExportMenuRaioX(false);
  };

  const exportarRaioXPNG = async () => {
    if (!raioXResultRef.current) return;
    const pv = parseFloat(raioXPV.replace(',', '.')) || 0;
    if (pv <= 0) return alert('Informe o preço de venda antes de exportar.');

    try {
      const canvas = await html2canvas(raioXResultRef.current, {
        backgroundColor: darkMode ? '#09090b' : '#ffffff',
        scale: 2,
      });
      const link = document.createElement('a');
      link.download = getExportFileName('RaioX', 'png');
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      alert('Erro ao gerar imagem: ' + err.message);
    }
    setShowExportMenuRaioX(false);
  };

  const exportarRaioXPDF = async () => {
    if (!raioXResultRef.current) return;
    const pv = parseFloat(raioXPV.replace(',', '.')) || 0;
    if (pv <= 0) return alert('Informe o preço de venda antes de exportar.');

    try {
      const canvas = await html2canvas(raioXResultRef.current, {
        backgroundColor: darkMode ? '#09090b' : '#ffffff',
        scale: 2,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(getExportFileName('RaioX', 'pdf'));
    } catch (err) {
      alert('Erro ao gerar PDF: ' + err.message);
    }
    setShowExportMenuRaioX(false);
  };

  const exportarCalculadoraExcel = () => {
    if (resultados.length === 0) return alert('Sem resultados para exportar.');
    const dados = resultados.map(r => {
      const res = r.resultado;
      return {
        'Canal': r.nome,
        'Custo Anúncio': custoAnuncio,
        'PV Projetado': res.pvProjetado || res.precoVenda,
        'Desconto (%)': res.descontoPercent ? (res.descontoPercent * 100).toFixed(1) + '%' : '0%',
        'PV Final': res.precoVenda,
        'Recebe': res.recebe,
        'Margem (R$)': res.margem,
        'Margem (%)': (res.margemPercent * 100).toFixed(2) + '%'
      };
    });
    const ws = XLSX.utils.json_to_sheet(dados);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Precificacao');
    ws['!cols'] = Object.keys(dados[0] || {}).map(() => ({ wch: 15 }));
    XLSX.writeFile(wb, getExportFileName('Calculadora', 'xlsx'));
    setShowExportMenuCalc(false);
  };

  const exportarCalculadoraPNG = async () => {
    if (!calculadoraResultRef.current) return;
    try {
      const canvas = await html2canvas(calculadoraResultRef.current, {
        backgroundColor: darkMode ? '#09090b' : '#ffffff',
        scale: 2,
      });
      const link = document.createElement('a');
      link.download = getExportFileName('Calculadora', 'png');
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) { alert('Erro ao gerar imagem: ' + err.message); }
    setShowExportMenuCalc(false);
  };

  const exportarCalculadoraPDF = async () => {
    if (!calculadoraResultRef.current) return;
    try {
      const canvas = await html2canvas(calculadoraResultRef.current, {
        backgroundColor: darkMode ? '#09090b' : '#ffffff',
        scale: 2,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(getExportFileName('Calculadora', 'pdf'));
    } catch (err) { alert('Erro ao gerar PDF: ' + err.message); }
    setShowExportMenuCalc(false);
  };


  const custoAnuncio = useMemo(() => {
    return produtosSel.reduce((acc, p) => acc + (p.custo * p.qtd), 0)
      + embalagensSel.reduce((acc, e) => acc + (e.custo * e.qtd), 0)
      + insumosSel.reduce((acc, i) => acc + (i.custo * i.qtd), 0);
  }, [produtosSel, embalagensSel, insumosSel]);

  // Peso (real) e dimensões do envio: peso = soma dos itens; dims = embalagem (maior volume).
  const dadosEnvio = useMemo(() => {
    const somaPeso = arr => arr.reduce((a, x) => a + ((Number(x.peso) || 0) * (x.qtd || 1)), 0);
    const pesoRealG = somaPeso(produtosSel) + somaPeso(embalagensSel) + somaPeso(insumosSel);
    const emb = embalagensSel.reduce((best, e) => {
      const vol = (Number(e.comprimento) || 0) * (Number(e.largura) || 0) * (Number(e.altura) || 0);
      const bvol = best ? (Number(best.comprimento) || 0) * (Number(best.largura) || 0) * (Number(best.altura) || 0) : -1;
      return vol > bvol ? e : best;
    }, null);
    const comp = Number(emb?.comprimento) || 0, larg = Number(emb?.largura) || 0, alt = Number(emb?.altura) || 0;
    const pesoRealKg = pesoRealG / 1000;
    const pesoVolKg = (comp * larg * alt) / 6000;
    return { pesoRealKg, comp, larg, alt, pesoTarifadoKg: Math.max(pesoRealKg, pesoVolKg) };
  }, [produtosSel, embalagensSel, insumosSel]);

  const resultados = useMemo(() => {
    const aplicar = (canalId, r) => {
      if (descontoAtivo && desconto > 0 && !r.erro) {
        const deveAplicar = aplicaDescontoEm === 'todos' || aplicaDescontoEm === canalId;
        if (deveAplicar) return aplicarDesconto(r, desconto, modoDesconto);
      }
      return r;
    };
    const m = canais.meli, s = canais.shopee;
    const comum = { custoAnuncio, modoMargem, margemAlvo, margemAlvoReais };
    const baseMeli = {
      ...comum, canal: 'meli',
      holding: m?.txHolding ?? 2, adsPct: (m?.ads ?? 0.03) * 100, impostoPct: (m?.imposto ?? 0.10) * 100,
      comissaoClassico: 12, comissaoPremium: 16.5, reputacao: 'green',
      pesoRealKg: dadosEnvio.pesoRealKg, pesoTarifadoKg: dadosEnvio.pesoTarifadoKg,
      comp: dadosEnvio.comp, larg: dadosEnvio.larg, alt: dadosEnvio.alt,
      full: fullAtivo, dias: fullDias, ofereceGratis: false
    };
    const out = [];
    if (m?.ativo) {
      out.push({ id: 'meli_classico', canalId: 'meli', nome: 'Mercado Livre · Clássico', params: { ...baseMeli, tipoAnuncio: 'classico' } });
      out.push({ id: 'meli_premium', canalId: 'meli', nome: 'Mercado Livre · Premium', params: { ...baseMeli, tipoAnuncio: 'premium' } });
    }
    if (s?.ativo) {
      out.push({
        id: 'shopee', canalId: 'shopee', nome: 'Shopee',
        params: { ...comum, canal: 'shopee', holding: s?.txHolding ?? 2, adsPct: (s?.ads ?? 0.03) * 100, impostoPct: (s?.imposto ?? 0.10) * 100 }
      });
    }
    return out.map(e => ({ ...e, resultado: aplicar(e.canalId, precoDeVenda(e.params)) }));
  }, [custoAnuncio, dadosEnvio, margemAlvo, margemAlvoReais, modoMargem, canais, fullAtivo, fullDias, descontoAtivo, desconto, modoDesconto, aplicaDescontoEm]);

  const melhorCanalId = useMemo(() => {
    const validos = resultados.filter(r => !r.resultado.erro);
    if (validos.length === 0) return null;
    return validos.reduce((best, curr) =>
      curr.resultado.margemPercent > (best?.resultado?.margemPercent || 0) ? curr : best
    )?.id;
  }, [resultados]);

  // Colunas dos cadastros. Peso (g) em todos; dimensões (cm) só na embalagem.
  const columnsBase = [
    { key: 'nome', label: 'Nome', type: 'text' },
    { key: 'sku', label: 'SKU', type: 'text' },
    { key: 'custo', label: 'Custo', type: 'number' },
    { key: 'unidade', label: 'Unidade', type: 'text' },
    { key: 'peso', label: 'Peso (g)', type: 'number' },
  ];
  const columnsAll = columnsBase; // compat
  const columnsProduto = columnsBase;
  const columnsInsumo = columnsBase;
  const columnsEmbalagem = [
    ...columnsBase,
    { key: 'comprimento', label: 'Compr. (cm)', type: 'number' },
    { key: 'largura', label: 'Larg. (cm)', type: 'number' },
    { key: 'altura', label: 'Alt. (cm)', type: 'number' },
  ];

  // Logout function
  const handleLogout = () => {
    logoutUser();
    setUser(null);
    setActiveView('calculadora');
  };

  // Enquanto verifica a sessão, evita piscar a landing page
  if (authLoading) {
    return <div style={{ minHeight: '100vh', background: t.bg }} />;
  }

  // Render Waiting Approval screen if user is logged in but not approved
  if (user && !user.approved) {
    return <WaitingApproval onLogout={handleLogout} t={t} />;
  }

  // Entry Point: If no user is logged in, show Landing Page
  if (!user) {
    return (
      <>
        <LandingPage
          onLoginClick={() => setIsAuthModalOpen(true)}
          t={t}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
        />
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
          onAuthSuccess={(record) => setUser(record)}
          t={t}
          closable={true}
        />
      </>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: t.bg, transition: 'all 0.3s ease', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Auth Modal (already handled by gatekeeper if !user, but kept for logic consistency) */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onAuthSuccess={(record) => setUser(record)}
        t={t}
      />
      <UserProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        user={user}
        onLogout={handleLogout}
        t={t}
      />

      <About
        isOpen={isAboutModalOpen}
        onClose={() => setIsAboutModalOpen(false)}
        t={t}
        isMobile={isMobile}
      />

      <div style={{ maxWidth: 1320, margin: '0 auto', padding: isMobile ? 12 : 24 }}>
        {/* Header */}
        <header style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          marginBottom: 28,
          paddingBottom: 20,
          borderBottom: `1.5px solid ${t.ink}`,
          gap: isMobile ? 16 : 0
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <h1 style={{ fontFamily: t.disp, fontSize: isMobile ? 22 : 26, fontWeight: 700, color: t.text, margin: 0, letterSpacing: '-0.02em' }}>Prexo</h1>
              <span style={{ fontFamily: t.mono, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: t.textMuted }}>precisão de preço</span>
            </div>
            {!isMobile && <p style={{ fontFamily: t.mono, fontSize: 9.5, color: t.textMuted, margin: '4px 0 0 0', letterSpacing: '0.08em', textTransform: 'uppercase' }}>v5.9.4 · calculadora de margem</p>}
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 8 : 16,
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'flex-start' : 'flex-end',
            width: isMobile ? '100%' : 'auto'
          }}>
            {/* Barra de Navegação */}
            <div style={{
              display: 'flex',
              border: `1.5px solid ${t.ink}`,
              flex: isMobile ? 1 : 'none'
            }}>
              {['calculadora', 'cadastros', 'sobre'].filter(view => !isMobile || view !== 'cadastros').map((view, i, arr) => (
                <button
                  key={view}
                  onClick={() => {
                    if (view === 'sobre') {
                      setIsAboutModalOpen(true);
                    } else {
                      setActiveView(view);
                    }
                  }}
                  style={{
                    flex: isMobile ? 1 : 'none',
                    padding: isMobile ? '9px 12px' : '9px 18px', border: 'none',
                    borderRight: i < arr.length - 1 ? `1.5px solid ${t.ink}` : 'none',
                    background: (activeView === view && view !== 'sobre') ? t.ink : 'transparent',
                    color: (activeView === view && view !== 'sobre') ? t.bg : t.textSecondary,
                    fontFamily: t.mono, fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: '0.06em', cursor: 'pointer', textTransform: 'uppercase',
                    transition: 'all 0.15s'
                  }}
                >
                  {view}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {(user?.role === 'admin' && !isMobile) && (
                <button
                  onClick={() => setActiveView('admin')}
                  style={{
                    width: 40, height: 40, borderRadius: 0,
                    border: `1.5px solid ${t.ink}`,
                    background: activeView === 'admin' ? t.ink : t.bgCard,
                    color: activeView === 'admin' ? t.bg : t.text,
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  title="Painel Admin"
                >
                  <Shield size={18} />
                </button>
              )}

              <button
                onClick={() => setDarkMode(!darkMode)}
                style={{
                  width: 40, height: 40, borderRadius: 0,
                  border: `1.5px solid ${t.ink}`, background: t.bgCard,
                  color: t.text, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
              >
                {darkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>
            </div>

            {/* User Profile / Login */}
            {user ? (
              <div
                onClick={() => setIsProfileModalOpen(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '6px 16px 6px 8px', borderRadius: 12,
                  background: t.bgCard, border: `1px solid ${t.border}`, cursor: 'pointer',
                  transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = t.bgInput;
                  e.currentTarget.style.borderColor = t.accent + '40';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = t.bgCard;
                  e.currentTarget.style.borderColor = t.border;
                }}
              >
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: t.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <User size={18} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: t.text, lineHeight: 1.2 }}>{user.name || 'Usuário'}</span>
                  <span style={{ fontSize: 10, color: t.textMuted }}>Ver Perfil</span>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setIsAuthModalOpen(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10,
                  border: `1px solid ${t.border}`, background: t.bgCard, color: t.text,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s'
                }}
              >
                <User size={16} />
                <span>Entrar</span>
              </button>
            )}
          </div>
        </header>

        {/* VIEW CONDICIONAL */}
        {activeView === 'admin' && user?.role === 'admin' ? (
          <AdminPanel t={t} />
        ) : activeView === 'calculadora' ? (
          <>
            {/* Sub-abas: Precificar | Raio-X */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
              <button
                onClick={() => setModoCalculadora('precificar')}
                style={{
                  padding: '10px 20px', borderRadius: 0,
                  border: `1.5px solid ${t.ink}`,
                  background: modoCalculadora === 'precificar' ? t.ink : 'transparent',
                  color: modoCalculadora === 'precificar' ? t.bg : t.textSecondary,
                  fontFamily: t.mono, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
                }}
              >
                Precificar
              </button>
              <button
                onClick={() => setModoCalculadora('raiox')}
                style={{
                  padding: '10px 20px', borderRadius: 0,
                  border: `1.5px solid ${t.ink}`,
                  background: modoCalculadora === 'raiox' ? t.ink : 'transparent',
                  color: modoCalculadora === 'raiox' ? t.bg : t.textSecondary,
                  fontFamily: t.mono, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8
                }}
              >
                <Search size={15} /> Raio-X
              </button>

              <button
                onClick={handleLimpar}
                style={{
                  padding: '10px 16px', borderRadius: 0,
                  border: `1.5px solid ${t.ink}`,
                  background: t.bgCard,
                  color: t.text,
                  fontFamily: t.mono, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                  marginLeft: 'auto',
                  transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = t.ink; e.currentTarget.style.color = t.bg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = t.bgCard; e.currentTarget.style.color = t.text; }}
                title="Limpar seleção"
              >
                <RotateCcw size={15} />
                {!isMobile && <span>Limpar</span>}
              </button>
            </div>

            {modoCalculadora === 'precificar' ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '280px minmax(0, 1fr)', gap: isMobile ? 16 : 28 }}>
                  {/* Sidebar */}
                  <div>
                    {/* Composição */}
                    <div style={{ background: t.bgCard, border: `1.5px solid ${t.ink}`, padding: '0 16px', marginBottom: 14 }}>
                      <Dropdown items={produtos} selecionados={produtosSel} onUpdate={setProdutosSel} label="Produtos" darkMode={darkMode} />
                      <Dropdown items={embalagens} selecionados={embalagensSel} onUpdate={setEmbalagensSel} label="Embalagens" darkMode={darkMode} />
                      <Dropdown items={insumos} selecionados={insumosSel} onUpdate={setInsumosSel} label="Insumos" darkMode={darkMode} showUnidade />
                    </div>

                    {/* Total */}
                    <div style={{ background: t.bgCard, border: `1.5px solid ${t.ink}`, padding: 16, marginBottom: 14, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                      <p style={{ fontFamily: t.mono, fontSize: 10, color: t.textMuted, margin: 0, textTransform: 'uppercase', letterSpacing: '0.14em' }}>Custo do anúncio</p>
                      <p style={{ fontSize: 22, fontWeight: 800, color: t.text, margin: 0, fontFamily: t.mono }}>{formatBRL(custoAnuncio)}</p>
                    </div>

                    {/* Full toggle (na comanda) */}
                    <div style={{ background: t.bgCard, border: `1.5px solid ${t.ink}`, padding: '12px 16px', marginBottom: 14 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontFamily: t.mono, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: t.textSecondary }}>
                        <span style={{ width: 16, height: 16, border: `1.5px solid ${t.ink}`, background: fullAtivo ? t.meli : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.meliInk, fontSize: 11, fontWeight: 700, flex: 'none' }}>{fullAtivo ? '✓' : ''}</span>
                        <input type="checkbox" checked={fullAtivo} onChange={e => setFullAtivo(e.target.checked)} style={{ display: 'none' }} />
                        Mercado Livre Full · 🟢 verde
                      </label>
                      {fullAtivo && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, fontFamily: t.mono, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: t.textMuted }}>
                          Dias em estoque
                          <input type="number" min="0" value={fullDias} onChange={e => setFullDias(+e.target.value || 0)} style={{ width: 60, padding: '5px 8px', border: `1.5px solid ${t.ink}`, background: t.bgInput, color: t.text, fontFamily: t.mono, textAlign: 'right' }} />
                        </div>
                      )}
                    </div>

                    {/* Margem */}
                    <div style={{ background: t.bgCard, border: `1.5px solid ${t.ink}`, padding: 18, marginBottom: 14 }}>
                      <p style={{ fontFamily: t.mono, fontSize: 10, color: t.textMuted, margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.14em' }}>Margem alvo</p>

                      <div style={{ display: 'flex', border: `1.5px solid ${t.ink}`, marginBottom: 16 }}>
                        {['percentual', 'reais'].map((modo, i) => (
                          <button
                            key={modo}
                            onClick={() => setModoMargem(modo)}
                            style={{
                              flex: 1, padding: '8px 0', border: 'none',
                              borderRight: i === 0 ? `1.5px solid ${t.ink}` : 'none',
                              background: modoMargem === modo ? t.ink : 'transparent',
                              color: modoMargem === modo ? t.bg : t.textSecondary,
                              fontFamily: t.mono, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            }}
                          >
                            {modo === 'percentual' ? '%' : 'R$'}
                          </button>
                        ))}
                      </div>

                      {modoMargem === 'percentual' ? (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <span style={{ fontSize: 13, color: t.textSecondary }}>Percentual</span>
                            <span style={{ fontSize: 16, fontWeight: 600, color: t.text, fontFamily: "'JetBrains Mono', monospace" }}>{formatPercent(margemAlvo)}</span>
                          </div>
                          <Slider
                            value={margemAlvo * 100}
                            onChange={(e) => setMargemAlvo(Number(e.target.value) / 100)}
                            min={5}
                            max={60}
                            darkMode={darkMode}
                          />
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={String(margemAlvoReais).replace('.', ',')}
                          onChange={(e) => setMargemAlvoReais(parseFloat(e.target.value.replace(',', '.')) || 0)}
                          style={{
                            width: '100%', padding: 12, borderRadius: 8, border: `1px solid ${t.border}`,
                            background: t.bgInput, color: t.text, fontSize: 18, fontWeight: 600,
                            textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", boxSizing: 'border-box',
                          }}
                        />
                      )}
                    </div>

                    {/* Desconto */}
                    <div style={{ background: t.bgCard, border: `1.5px solid ${t.ink}`, padding: 18 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: descontoAtivo ? 16 : 0 }}>
                        <p style={{ fontFamily: t.mono, fontSize: 10, color: t.textMuted, margin: 0, textTransform: 'uppercase', letterSpacing: '0.14em' }}>Desconto</p>
                        <label style={{ position: 'relative', width: 46, height: 24, cursor: 'pointer' }}>
                          <input type="checkbox" checked={descontoAtivo} onChange={(e) => setDescontoAtivo(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
                          <span style={{ position: 'absolute', inset: 0, background: descontoAtivo ? t.ink : 'transparent', border: `1.5px solid ${t.ink}`, borderRadius: 12, transition: 'background 0.2s' }}>
                            <span style={{ position: 'absolute', top: 2, left: descontoAtivo ? 24 : 2, width: 16, height: 16, background: descontoAtivo ? t.bg : t.ink, borderRadius: 8, transition: 'left 0.2s' }} />
                          </span>
                        </label>
                      </div>

                      {descontoAtivo && (
                        <>
                          <div style={{ marginBottom: 16 }}>
                            <p style={{ fontSize: 12, color: t.textSecondary, margin: '0 0 8px 0' }}>Aplicar em</p>
                            <div style={{ display: 'flex', gap: 6 }}>
                              {[{ id: 'todos', label: 'Todos' }, { id: 'meli', label: 'MELI' }, { id: 'shopee', label: 'Shopee' }].map(opt => (
                                <button
                                  key={opt.id}
                                  onClick={() => setAplicaDescontoEm(opt.id)}
                                  style={{
                                    flex: 1, padding: '8px 0', borderRadius: 6,
                                    border: aplicaDescontoEm === opt.id ? 'none' : `1px solid ${t.border}`,
                                    background: aplicaDescontoEm === opt.id ? t.accent : 'transparent',
                                    color: aplicaDescontoEm === opt.id ? '#fff' : t.textMuted,
                                    fontSize: 12, cursor: 'pointer',
                                  }}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div style={{ marginBottom: 16 }}>
                            <p style={{ fontSize: 12, color: t.textSecondary, margin: '0 0 8px 0' }}>Modalidade</p>
                            {[{ id: 'direto', label: 'Direto', desc: 'Reduz PV, consome margem' }, { id: 'preservar', label: 'Preservar margem', desc: 'Exibe preço "de" maior' }].map(opt => (
                              <label
                                key={opt.id}
                                style={{
                                  display: 'flex', alignItems: 'flex-start', gap: 8,
                                  padding: 10, marginBottom: 6, borderRadius: 6,
                                  background: modoDesconto === opt.id ? (darkMode ? '#3b82f610' : '#3b82f610') : 'transparent',
                                  border: modoDesconto === opt.id ? `1px solid ${t.accent}40` : `1px solid ${t.border}`,
                                  cursor: 'pointer',
                                }}
                              >
                                <input type="radio" checked={modoDesconto === opt.id} onChange={() => setModoDesconto(opt.id)} style={{ marginTop: 2, accentColor: t.accent }} />
                                <div>
                                  <span style={{ fontSize: 13, color: t.text, display: 'block' }}>{opt.label}</span>
                                  <span style={{ fontSize: 11, color: t.textMuted }}>{opt.desc}</span>
                                </div>
                              </label>
                            ))}
                          </div>

                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                              <span style={{ fontSize: 12, color: t.textSecondary }}>Percentual</span>
                              <span style={{ fontSize: 14, fontWeight: 600, color: t.text, fontFamily: "'JetBrains Mono', monospace" }}>{formatPercent(desconto)}</span>
                            </div>
                            <Slider
                              value={desconto * 100}
                              onChange={(e) => setDesconto(Number(e.target.value) / 100)}
                              min={0}
                              max={50}
                              darkMode={darkMode}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Resultados Area */}
                  <div ref={calculadoraResultRef}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
                        <h2 style={{ fontFamily: t.mono, fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.text, margin: 0 }}>Resultados</h2>
                        {dadosEnvio.pesoTarifadoKg > 0 && <span style={{ fontFamily: t.mono, fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.textMuted }}>peso tarifado {dadosEnvio.pesoTarifadoKg.toFixed(2).replace('.', ',')} kg</span>}
                      </div>

                      {/* Dropdown de Exportação Calculadora */}
                      <div style={{ position: 'relative' }}>
                        <button
                          onClick={() => setShowExportMenuCalc(!showExportMenuCalc)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                            background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8,
                            color: t.text, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                          }}
                        >
                          <Download size={14} /> Exportar <ChevronDown size={12} />
                        </button>

                        {showExportMenuCalc && (
                          <div style={{
                            position: 'absolute', top: '100%', right: 0, marginTop: 4,
                            background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10, minWidth: 160,
                            overflow: 'hidden',
                          }}>
                            <button onClick={exportarCalculadoraExcel} style={{
                              width: '100%', padding: '12px 16px', border: 'none', background: 'transparent',
                              color: t.text, fontSize: 13, textAlign: 'left', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 10,
                            }}
                              onMouseEnter={(e) => e.target.style.background = t.bgInput}
                              onMouseLeave={(e) => e.target.style.background = 'transparent'}
                            >
                              <FileSpreadsheet size={16} /> Excel (.xlsx)
                            </button>
                            <button onClick={exportarCalculadoraPNG} style={{
                              width: '100%', padding: '12px 16px', border: 'none', background: 'transparent',
                              color: t.text, fontSize: 13, textAlign: 'left', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 10,
                              borderTop: `1px solid ${t.border}`,
                            }}
                              onMouseEnter={(e) => e.target.style.background = t.bgInput}
                              onMouseLeave={(e) => e.target.style.background = 'transparent'}
                            >
                              🖼️ Imagem (.png)
                            </button>
                            <button onClick={exportarCalculadoraPDF} style={{
                              width: '100%', padding: '12px 16px', border: 'none', background: 'transparent',
                              color: t.text, fontSize: 13, textAlign: 'left', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 10,
                              borderTop: `1px solid ${t.border}`,
                            }}
                              onMouseEnter={(e) => e.target.style.background = t.bgInput}
                              onMouseLeave={(e) => e.target.style.background = 'transparent'}
                            >
                              📄 PDF (.pdf)
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Resumo do Anúncio */}
                    <ResumoAnuncio
                      produtosSel={produtosSel}
                      embalagensSel={embalagensSel}
                      insumosSel={insumosSel}
                      darkMode={darkMode}
                      isMobile={isMobile}
                      onUpdateProdutos={setProdutosSel}
                      onUpdateEmbalagens={setEmbalagensSel}
                      onUpdateInsumos={setInsumosSel}
                    />

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(232px, 1fr))', gap: 16, alignContent: 'start', marginBottom: 24 }}>
                      {resultados.map(res => (
                        <CanalCard key={res.id} canal={{ nome: res.nome, ativo: true }} resultado={res.resultado} isBest={melhorCanalId === res.id} darkMode={darkMode} isMobile={isMobile} />
                      ))}
                    </div>

                    {/* assinatura: código de barras */}
                    {resultados.length > 0 && (
                      <div style={{ textAlign: 'center', margin: '6px 0 28px' }}>
                        <div className="px-barcode" style={{ margin: '0 auto 8px' }} />
                        <div style={{ fontFamily: t.mono, fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.textMuted }}>Prexo · extrato de precificação</div>
                      </div>
                    )}

                    {/* Simulação de Preço */}
                    <SimulacaoPreco resultados={resultados} custoAnuncio={custoAnuncio} canais={canais} darkMode={darkMode} isMobile={isMobile} />
                  </div>
                </div>
              </>
            ) : (
              /* ========== RAIO-X DE PREÇO ========== */
              <div>
                <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <h2 style={{ fontFamily: t.disp, fontSize: 20, fontWeight: 700, color: t.text, margin: 0, letterSpacing: '-0.01em' }}>Raio-X de Preço</h2>
                    <p style={{ fontFamily: t.mono, fontSize: 10, color: t.textMuted, margin: '4px 0 0 0', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      Análise reversa · quanto sobra no preço atual
                    </p>
                  </div>

                  {/* Dropdown de Exportação */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setShowExportMenuRaioX(!showExportMenuRaioX)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
                        background: t.bgCard, border: `1.5px solid ${t.ink}`, borderRadius: 0,
                        color: t.text, fontFamily: t.mono, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
                      }}
                    >
                      <Download size={15} /> Exportar <ChevronDown size={13} />
                    </button>

                    {showExportMenuRaioX && (
                      <div style={{
                        position: 'absolute', top: '100%', right: 0, marginTop: 4,
                        background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10, minWidth: 160,
                        overflow: 'hidden',
                      }}>
                        <button onClick={exportarRaioXExcel} style={{
                          width: '100%', padding: '12px 16px', border: 'none', background: 'transparent',
                          color: t.text, fontSize: 13, textAlign: 'left', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 10,
                        }}
                          onMouseEnter={(e) => e.target.style.background = t.bgInput}
                          onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                          <FileSpreadsheet size={16} /> Excel (.xlsx)
                        </button>
                        <button onClick={exportarRaioXPNG} style={{
                          width: '100%', padding: '12px 16px', border: 'none', background: 'transparent',
                          color: t.text, fontSize: 13, textAlign: 'left', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 10,
                          borderTop: `1px solid ${t.border}`,
                        }}
                          onMouseEnter={(e) => e.target.style.background = t.bgInput}
                          onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                          🖼️ Imagem (.png)
                        </button>
                        <button onClick={exportarRaioXPDF} style={{
                          width: '100%', padding: '12px 16px', border: 'none', background: 'transparent',
                          color: t.text, fontSize: 13, textAlign: 'left', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 10,
                          borderTop: `1px solid ${t.border}`,
                        }}
                          onMouseEnter={(e) => e.target.style.background = t.bgInput}
                          onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                          📄 PDF (.pdf)
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '280px minmax(0, 1fr)', gap: isMobile ? 16 : 28 }}>
                  {/* Sidebar Raio-X */}
                  <div>
                    <div style={{ background: t.bgCard, border: `1.5px solid ${t.ink}`, padding: 18 }}>
                      {/* Preço de Venda */}
                      <div style={{ marginBottom: 18 }}>
                        <label style={{ display: 'block', fontFamily: t.mono, fontSize: 10, color: t.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.14em' }}>
                          Preço de venda
                        </label>
                        <input
                          type="text"
                          placeholder="Ex: 49,90"
                          value={raioXPV}
                          onChange={(e) => setRaioXPV(e.target.value.replace(/[^\d,]/g, ''))}
                          style={{
                            width: '100%', padding: 14, border: `1.5px solid ${t.ink}`,
                            background: t.bgInput, color: t.text, fontSize: 24, fontWeight: 800,
                            textAlign: 'center', fontFamily: t.mono, boxSizing: 'border-box',
                          }}
                        />
                      </div>

                      {/* Custo do Produto */}
                      <div style={{ marginBottom: 18 }}>
                        <label style={{ display: 'block', fontFamily: t.mono, fontSize: 10, color: t.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.14em' }}>
                          Custo do produto
                        </label>
                        <input
                          type="text"
                          placeholder="Ex: 22,00"
                          value={raioXCusto}
                          onChange={(e) => setRaioXCusto(e.target.value.replace(/[^\d,]/g, ''))}
                          style={{
                            width: '100%', padding: 12, border: `1.5px solid ${t.ink}`,
                            background: t.bgInput, color: t.text, fontSize: 18, fontWeight: 700,
                            textAlign: 'center', fontFamily: t.mono, boxSizing: 'border-box',
                          }}
                        />
                      </div>

                      {/* Seletor de Canal */}
                      <div style={{ marginBottom: 20 }}>
                        <label style={{ display: 'block', fontFamily: t.mono, fontSize: 10, color: t.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.14em' }}>
                          Analisar em
                        </label>
                        <div style={{ display: 'flex', border: `1.5px solid ${t.ink}` }}>
                          {[{ id: 'ambos', label: 'Ambos' }, { id: 'meli', label: 'MELI' }, { id: 'shopee', label: 'Shopee' }].map((opt, i, arr) => (
                            <button
                              key={opt.id}
                              onClick={() => setRaioXCanal(opt.id)}
                              style={{
                                flex: 1, padding: '9px 0', border: 'none',
                                borderRight: i < arr.length - 1 ? `1.5px solid ${t.ink}` : 'none',
                                background: raioXCanal === opt.id ? t.ink : 'transparent',
                                color: raioXCanal === opt.id ? t.bg : t.textSecondary,
                                fontFamily: t.mono, fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', cursor: 'pointer',
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Botão Limpar */}
                      <button
                        onClick={() => { setRaioXPV(''); setRaioXCusto(''); }}
                        style={{
                          width: '100%', padding: '10px 0', borderRadius: 6,
                          border: `1px solid ${t.border}`, background: 'transparent',
                          color: t.textMuted, fontSize: 13, cursor: 'pointer',
                        }}
                      >
                        Limpar
                      </button>
                    </div>
                  </div>

                  {/* Resultados Raio-X */}
                  <div ref={raioXResultRef}>
                    {(() => {
                      const pv = parseFloat(raioXPV.replace(',', '.')) || 0;
                      const custo = parseFloat(raioXCusto.replace(',', '.')) || 0;

                      if (pv <= 0) {
                        return (
                          <div style={{
                            background: t.bgCard, borderRadius: 12, border: `1px solid ${t.border}`,
                            padding: 40, textAlign: 'center'
                          }}>
                            <p style={{ fontSize: 48, margin: '0 0 16px 0' }}>🔍</p>
                            <p style={{ fontSize: 16, color: t.textMuted, margin: 0 }}>
                              Informe o preço de venda para analisar
                            </p>
                          </div>
                        );
                      }

                      const mC = canais.meli, sC = canais.shopee;
                      const baseMeli = {
                        canal: 'meli', custoAnuncio: custo, modoMargem: 'percentual',
                        holding: mC?.txHolding ?? 2, adsPct: (mC?.ads ?? 0.03) * 100, impostoPct: (mC?.imposto ?? 0.10) * 100,
                        comissaoClassico: 12, comissaoPremium: 16.5, reputacao: 'green',
                        pesoRealKg: dadosEnvio.pesoRealKg, pesoTarifadoKg: dadosEnvio.pesoTarifadoKg,
                        comp: dadosEnvio.comp, larg: dadosEnvio.larg, alt: dadosEnvio.alt,
                        full: fullAtivo, dias: fullDias, ofereceGratis: false
                      };
                      const resultadosRaioX = [];
                      if ((raioXCanal === 'ambos' || raioXCanal === 'meli') && mC?.ativo) {
                        resultadosRaioX.push({ id: 'meli_classico', nome: 'Mercado Livre · Clássico', resultado: resultadoNoPreco({ ...baseMeli, tipoAnuncio: 'classico' }, pv) });
                        resultadosRaioX.push({ id: 'meli_premium', nome: 'Mercado Livre · Premium', resultado: resultadoNoPreco({ ...baseMeli, tipoAnuncio: 'premium' }, pv) });
                      }
                      if ((raioXCanal === 'ambos' || raioXCanal === 'shopee') && sC?.ativo) {
                        resultadosRaioX.push({ id: 'shopee', nome: 'Shopee', resultado: resultadoNoPreco({ canal: 'shopee', custoAnuncio: custo, holding: sC?.txHolding ?? 2, adsPct: (sC?.ads ?? 0.03) * 100, impostoPct: (sC?.imposto ?? 0.10) * 100 }, pv) });
                      }
                      const melhorMargem = resultadosRaioX.reduce((best, curr) =>
                        !best || (curr.resultado.margem > best.resultado.margem) ? curr : best
                        , null);

                      return (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(232px, 1fr))', gap: 16, alignContent: 'start' }}>
                            {resultadosRaioX.map(({ id, nome, resultado }) => (
                              <CanalCard key={id} canal={{ nome, ativo: true }} resultado={resultado} isBest={melhorMargem?.id === id && resultadosRaioX.length > 1} darkMode={darkMode} isMobile={isMobile} />
                            ))}
                          </div>

                          {resultadosRaioX.length > 1 && melhorMargem && (
                            <div style={{ marginTop: 18, background: t.bgCard, border: `1.5px solid ${t.ink}`, padding: '12px 16px', textAlign: 'center', fontFamily: t.mono, fontSize: 12, color: t.textSecondary }}>
                              Melhor canal: <strong style={{ color: t.profit }}>{melhorMargem.nome}</strong>
                              {' '}(+{formatBRL(melhorMargem.resultado.margem - Math.min(...resultadosRaioX.map(r => r.resultado.margem)))} de diferença)
                            </div>
                          )}

                          <div style={{ textAlign: 'center', margin: '24px 0 8px' }}>
                            <div className="px-barcode" style={{ margin: '0 auto 8px' }} />
                            <div style={{ fontFamily: t.mono, fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.textMuted }}>Prexo · raio-x de preço</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div>
            <div style={{ display: 'flex', border: `1.5px solid ${t.ink}`, width: 'fit-content', marginBottom: 24 }}>
              {['produtos', 'embalagens', 'insumos', 'canais'].map((tab, i, arr) => (
                <button
                  key={tab}
                  onClick={() => setActiveAdmin(tab)}
                  style={{
                    padding: '10px 20px', border: 'none',
                    borderRight: i < arr.length - 1 ? `1.5px solid ${t.ink}` : 'none',
                    background: activeAdmin === tab ? t.ink : 'transparent',
                    color: activeAdmin === tab ? t.bg : t.textSecondary,
                    fontFamily: t.mono, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', cursor: 'pointer', textTransform: 'uppercase',
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>

            {activeAdmin === 'produtos' && <AdminTable items={produtos} setItems={setProdutos} columns={columnsProduto} title="Produtos" darkMode={darkMode} isMobile={isMobile} />}
            {activeAdmin === 'embalagens' && <AdminTable items={embalagens} setItems={setEmbalagens} columns={columnsEmbalagem} title="Embalagens" darkMode={darkMode} isMobile={isMobile} />}
            {activeAdmin === 'insumos' && <AdminTable items={insumos} setItems={setInsumos} columns={columnsInsumo} title="Insumos" darkMode={darkMode} isMobile={isMobile} />}
            {activeAdmin === 'canais' && <CanaisAdmin canais={canais} setCanais={setCanais} darkMode={darkMode} isMobile={isMobile} />}
          </div>
        )}
      </div >
    </div >
  );
}
